import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { endOfMonth, startOfMonth } from 'date-fns';
import { isUndefined } from 'lodash-es';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { environment } from '@env';
import { constants } from '@shared/constants';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { SKIP_ERROR_NOTIFICATION } from '@shared/interceptors';
import {
  AISearchResultsRaw,
  AddAskMeAnythingQuestionParam,
  AddScheduledReportParams,
  AskMeAnythingQuestion,
  BusinessAccount,
  InsightDocumentList,
  InsightDocumentRaw,
  InsightReportHistoryStatus,
  InsightStatementPageParams,
  ReportExtractType,
  ReportLinkResponse,
  ReportList,
  ReportListRaw,
  ReportTemplate,
  RequestPageParams,
  ScheduledReport,
  SupersetConfig,
} from '@shared/models';
import { APP_ENV_CONFIG, BusinessAccountService } from '@shared/services';
import { BackendService } from '@shared/services/backend.service';
import { selectFeatureFlagEnabledByKey } from '@shared/store';

import { reportRequestParams } from './report-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  envConfig = inject(APP_ENV_CONFIG);

  constructor(
    private backendService: BackendService,
    private businessAccountService: BusinessAccountService,
    private store: Store,
  ) {}

  private getSuppressErrorContext(): Observable<HttpContext> {
    return this.store.select(selectFeatureFlagEnabledByKey('suppress-disabled-features-errors')).pipe(
      take(1),
      map((shouldSuppress) => (shouldSuppress ? new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) : new HttpContext())),
    );
  }

  public getReports(
    activeBusinessAccountId: BusinessAccount['id'],
    { page, size, sortParams, activeFilters }: RequestPageParams,
  ): Observable<ReportList> {
    const pageSize = size ?? constants.TABLE_ROWS;
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('size', pageSize);

    params = params.append('businessAccountId', activeBusinessAccountId);

    if (page) {
      params = params.append('page', page);
    }

    if (activeFilters) {
      const { extractType, yearFrom, monthFrom, yearTo, monthTo, financialAccountId } = activeFilters;
      if (extractType) {
        params = params.append('extractType', extractType);
      }

      if (financialAccountId) {
        params = params.append('financialAccountId', financialAccountId);
      }

      if (yearFrom && !isUndefined(monthFrom)) {
        params = params.append(
          'extractCreatedDateFrom',
          startOfMonth(new Date(parseInt(yearFrom, 10), parseInt(monthFrom, 10))).toISOString(),
        );
      }

      if (yearTo && !isUndefined(monthTo)) {
        params = params.append('extractCreatedDateTo', endOfMonth(new Date(parseInt(yearTo, 10), parseInt(monthTo, 10))).toISOString());
      }
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    return this.backendService.get<ReportListRaw>(`${environment.insightsEndpoint}/reports`, { params }).pipe(
      map((response) => {
        const { content = [], totalElements = 0 } = response || {};

        return {
          items: content?.map((item) => item),
          totalElements,
        };
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getInsightReports(requestParams: RequestPageParams): Observable<InsightDocumentList> {
    let params = reportRequestParams(requestParams);

    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.getSelectedBusinessAccountId().pipe(
          switchMap((businessAccountId) => {
            params = params.append('businessAccountId', businessAccountId);
            return this.backendService.get<InsightDocumentRaw>(`${environment.insightsEndpoint}/reports`, { params, context }).pipe(
              map((response) => {
                const { content = [], totalElements = 0, totalPages = 0 } = response || {};

                return {
                  items: content?.map((item) => item),
                  totalElements,
                  totalPages,
                };
              }),
              catchError((errorRes) => throwError(() => errorRes)),
            );
          }),
        ),
      ),
    );
  }

  public getInsightReportPreSignedURL(reportId: string, fileType: string): Observable<ReportLinkResponse> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<ReportLinkResponse>(`${environment.insightsEndpoint}/reports/${reportId}/${fileType}`, { context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getInsightReportTypes(): Observable<ReportExtractType[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) => this.backendService.get<ReportExtractType[]>(`${environment.insightsEndpoint}/reports/types`, { context })),
      map((reportTypes) => {
        return reportTypes?.map((reportType) => ({
          ...reportType,
          value: reportType?.id,
        }));
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getInsightReportStatus(): Observable<InsightReportHistoryStatus[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<InsightReportHistoryStatus[]>(`${environment.insightsEndpoint}/reports/latest-history`, { context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public updateInsightReportStatus(sectionName: string): Observable<void> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.update<void>(`${environment.insightsEndpoint}/reports/latest-history/sections/${sectionName}`, undefined, {
          context,
        }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getScheduledReports({ searchString }: RequestPageParams): Observable<ScheduledReport[]> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('isInternal', false);

    if (searchString) {
      params = params.append('name', searchString);
    }
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<ScheduledReport[]>(`${environment.insightsEndpoint}/reports/schedulers`, { params, context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getScheduledReportTemplates(): Observable<ReportTemplate[]> {
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('isInternal', false);

    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<ReportTemplate[]>(`${environment.insightsEndpoint}/reports/templates`, { params, context }),
      ),
      map((reportTemplates) => {
        return reportTemplates?.map((reportTemplate) => ({
          ...reportTemplate,
          label: reportTemplate.name,
          value: reportTemplate?.id,
        }));
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public createScheduledReport(scheduledReportData: AddScheduledReportParams): Observable<string> {
    return this.backendService.post<string>(`${environment.insightsEndpoint}/reports/schedulers`, scheduledReportData).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public updateScheduledReport(scheduledReportId: string, reportData: Partial<ScheduledReport>): Observable<boolean> {
    return this.backendService.patch(`${environment.insightsEndpoint}/reports/schedulers/${scheduledReportId}`, reportData).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public deleteScheduledReport(scheduledReportId: string): Observable<boolean> {
    return this.backendService.delete<void>(`${environment.insightsEndpoint}/reports/schedulers/${scheduledReportId}`).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getInsightStatements(requestParams: InsightStatementPageParams): Observable<InsightDocumentList> {
    const params = reportRequestParams(requestParams);

    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<InsightDocumentRaw>(`${environment.insightsEndpoint}/reports/statements`, { params, context }),
      ),
      map((response) => {
        const { content = [], totalElements = 0, totalPages = 0 } = response || {};

        return {
          items: content?.map((item) => {
            return {
              ...item,
              downloadAvailable: !!item.pdfStatementPath || !!item.csvStatementPath,
            };
          }),
          totalElements,
          totalPages,
        };
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getAISearchResults(search?: string): Observable<AISearchResultsRaw> {
    if (!search) {
      return of({});
    }
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.post<AISearchResultsRaw>(`${environment.insightsEndpoint}/english-to-insights`, { query: search }, { context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getAskMeAnythingQuestions(): Observable<AskMeAnythingQuestion[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<AskMeAnythingQuestion[]>(`${environment.insightsEndpoint}/english-to-insights/favourites`, { context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public addAskMeAnythingQuestion(query: AddAskMeAnythingQuestionParam): Observable<AskMeAnythingQuestion[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.post<AskMeAnythingQuestion[]>(`${environment.insightsEndpoint}/english-to-insights/favourites`, query, {
          context,
        }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public deleteAskMeAnythingQuestion(queryId: string): Observable<boolean> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.delete<void>(`${environment.insightsEndpoint}/english-to-insights/favourites/${queryId}`, { context }),
      ),
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public supersetInitConfig(dashboardName: string): Observable<SupersetConfig> {
    const headers = new HttpHeaders().set('Content-Type', `application/x-www-form-urlencoded`);
    const urlencoded = new URLSearchParams();
    urlencoded.append('dashboardName', dashboardName);

    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.post<SupersetConfig>(`${environment.insightsEndpoint}/superset`, urlencoded, { headers, context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  private getSelectedBusinessAccountId(): Observable<BusinessAccount['id']> {
    return this.businessAccountService.getSelectedBusinessAccountId();
  }
}
